<template>
  <div class="min-h-screen bg-true-gray-900 dark:bg-true-gray-900">
    <div v-if="!isInitializing" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-warm-gray-400 py-4 sm:pt-8 sm:pb-20 pb-20">
      <div class="flex items-center justify-between gap-3 mb-8 sm:mb-12">
        <div class="min-w-0">
          <div id="limited-time-offers" class="sr-only"></div>
          <h2 class="text-base font-semibold tracking-wider text-yellow-400 uppercase">
            Access
          </h2>
          <h1 class="text-2xl sm:text-3xl lg:text-4xl text-true-gray-300 font-bold">
            Reviewers
          </h1>
        </div>
        <button @click="navigateTo('/purchase')" type="button"
          class="flex-shrink-0 px-3 sm:px-6 text-center whitespace-nowrap text-sm sm:text-base rounded-md border-yellow-500 border-2 py-2.5 sm:py-3 font-semibold text-yellow-500 hover:bg-yellow-900 hover:bg-opacity-20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-true-gray-500">
          Buy A Subscription &rarr;
        </button>
      </div>
      <div class="text-true-gray-300 bg-true-gray-900 grid grid-cols-1 gap-8">
        <ReviewerCard v-for="reviewer in reviewers" :key="reviewer.id" :reviewer="reviewer"
          @handleActivateModal="handleActivateModal" />
      </div>
      <ActivateModal :isActivateModalOpen="isActivateModalOpen" @closeActivateModal="isActivateModalOpen = false"
        :user="userInfo" :reviewer="chosenReviewer" :refetchUserInfo="refetchUserInfo" />
    </div>
    <div v-else
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-warm-gray-400 text-warm-gray-400 py-8 space-y-8 mb-20">
      <div class="text-true-gray-500 bg-true-gray-900 flex items-center justify-center">
        <svg class="mr-2 w-5 h-4 animate-spin text-true-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
          </path>
        </svg>
        Loading your reviewers...
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
// Firebase
import firebase from "@/firebase/config";
// Helpers
import { REVIEWERS_DATA } from './helpers/data';
import { buildReviewerUserData } from './helpers/user';

// Components
import ReviewerCard from './components/ReviewerCard.vue';
import ActivateModal from './components/ActivateModal.vue';

import caapKnowledge from '@/assets/images/caap-primary-photo.jpeg';
import caapPractical from '@/assets/images/plus-primary-photo.jpeg';
import caap101 from '@/assets/images/lite-primary-photo.jpeg';
import entranceExam from '@/assets/images/mtp-primary-photo.jpeg';

const router = useRouter();

const reviewers = ref(REVIEWERS_DATA);
const userInfo = ref(null);
const isWaiting = ref(false);
const isActivateModalOpen = ref(false);
const chosenReviewer = ref(null);
let unsubscribeReviewerListener = null;

const handleActivateModal = (reviewer) => {
  chosenReviewer.value = reviewer;
  isActivateModalOpen.value = true;
  console.log('Chosen reviewer:', chosenReviewer.value);
  console.log('User info:', userInfo.value);
}

const isInitializing = ref(true);

onMounted(async () => {
  isInitializing.value = true;
  console.log('%c Reviewers page mounted', 'color: yellow; font-weight: bold;');
  userInfo.value = await fetchUserInfo();
  initializeUI(userInfo.value);
  isInitializing.value = false;
  /**
   * attach the images for each reviewer
   */
  reviewers.value.forEach(reviewer => {
    if (reviewer.oldId === 'caap') {
      reviewer.imageSrc = caapKnowledge;
    } else if (reviewer.oldId === 'caap_plus') {
      reviewer.imageSrc = caapPractical;
    } else if (reviewer.oldId === 'caap-101') {
      reviewer.imageSrc = caap101;
    } else if (reviewer.oldId === 'mtp') {
      reviewer.imageSrc = entranceExam;
    }
  });

  /**
   * Setup a reviewer status listener.
   * On update/change, fetch the new 
   * user info and reinitialize ui.
   * OnUnmount, remove listener.
   */
  setupReviewerStatusListener();
});

onUnmounted(() => {
  // Clean up the listener when component unmounts
  if (unsubscribeReviewerListener) {
    unsubscribeReviewerListener();
    console.log('%c Reviewer status listener cleaned up', 'color: orange; font-weight: bold;');
  }
});

const navigateTo = (path) => {
  router.push(path);
}

const refetchUserInfo = async () => {
  userInfo.value = await fetchUserInfo();
  initializeUI(userInfo.value);
}

/**
 * Setup a real-time listener for changes to the user's reviewer status.
 * This will automatically update the UI whenever the reviewers object changes.
 */
const setupReviewerStatusListener = () => {
  const userId = firebase.auth().currentUser.uid;

  unsubscribeReviewerListener = firebase
    .firestore()
    .collection("users")
    .doc(userId)
    .onSnapshot(
      (doc) => {
        if (doc.exists) {
          const data = doc.data();
          console.log('%c Reviewer status changed', 'color: cyan; font-weight: bold;', data.reviewers);

          // Update userInfo with new data
          userInfo.value = { ...data, id: doc.id };

          // Reinitialize UI with updated data
          initializeUI(userInfo.value);
        }
      },
      (error) => {
        console.error("Error listening to reviewer status changes:", error);
      }
    );
}

/**
 * Get the user data or information form database.
 * This is a promise.
 */
const fetchUserInfo = async () => {
  return firebase.firestore().collection("users").doc(firebase.auth().currentUser.uid).get()
    .then(doc => {
      let data = doc.data()
      console.log('====>', data);
      data.id = doc.id
      return data
    })
    .catch((error) => {
      console.error("Error getting user document:", error);
      throw `Error getting user document: ${error}`;
    });
};

/**
 * Initialize UI by mapping user status to each reviewer
 * @param {Object} user - User data from database
 */
const initializeUI = (user) => {
  if (!user) {
    console.warn('User data not available');
    return;
  }

  // Create a copy of reviewers to avoid mutating the original
  const updatedReviewers = reviewers.value.map(reviewer => ({ ...reviewer }));

  /**
   * These are unused.
   */
  // Find indices for special reviewers
  // const caap101Index = updatedReviewers.findIndex(r => r.oldId === 'caap-101');
  // const caapKnowledgeIndex = updatedReviewers.findIndex(r => r.claimTag === 'caap');
  // const caapPracticalIndex = updatedReviewers.findIndex(r => r.claimTag === 'caap_plus');
  // const caapLiteIndex = updatedReviewers.findIndex(r => r.claimTag === 'caap_lite');

  // Process each reviewer and map user status
  updatedReviewers.forEach((reviewer, index) => {

    /**
     * Since CAAP 101 is now accessible to all users,
     * we don't need to check for it.
     * We can just set the status to ACTIVE.
     */
    if (reviewer.oldId === 'caap-101') {
      reviewer.userData = {
        subscriptionStatus: 'ACTIVE',
        refCode: null,
        hasPreviousSubscription: null,
        expirationDate: null,
        durationInWords: null,
        duration: null,
      };
      return;
    }

    // Build user data for reviewers with claimTag
    if (reviewer.claimTag) {
      reviewer.userData = buildReviewerUserData(user, reviewer.claimTag);

      /**
       * Since this page will only show the reviewers, 
       * we don't need to check for bundle card visibility.
       * This is done in the "Purchase" page later.
       */
      // Check bundle card visibility (show if both CAAP Knowledge and Practical are INACTIVE)
      // if (reviewer.claimTag === 'caap' || reviewer.claimTag === 'caap_plus') {
      //   isBundleCardShow.value = isBundleCardShow.value && 
      //     reviewer.userData.subscriptionStatus === 'INACTIVE';
      // }
    } else {
      // Fallback for reviewers without claimTag
      reviewer.userData = {
        subscriptionStatus: 'INACTIVE',
        refCode: null,
        hasPreviousSubscription: null,
        expirationDate: null,
        durationInWords: null,
        duration: null,
      };
    }
  });

  /**
   * As of November 18, 2025, CAAP 101 is now accessible to all users.
   * So we don't need to check for CAAP Knowledge and/or Practical.
   * We can just set the status to ACTIVE.
   * 
   * updatedReviewers[caap101Index].userData.subscriptionStatus = 'ACTIVE';
   */
  // Special case: CAAP 101 status is derived from CAAP Knowledge and/or Practical
  // if (caap101Index > -1 && caapKnowledgeIndex > -1 && caapPracticalIndex > -1) {
  //   const knowledgeStatus = updatedReviewers[caapKnowledgeIndex].userData.subscriptionStatus;
  //   const practicalStatus = updatedReviewers[caapPracticalIndex].userData.subscriptionStatus;

  //   if (knowledgeStatus !== 'INACTIVE' || practicalStatus !== 'INACTIVE') {
  //     updatedReviewers[caap101Index].userData.subscriptionStatus = 'ACTIVE';
  //   }
  // }

  /**
   * As of November 18, 2025, Caap Lite is no longer available.
   * So we don't need to check for Caap Lite.
   * We can just remove it from the reviewers list.
   */
  // Special case: CAAP Lite - remove if not active, set expiration if active
  // if (caapLiteIndex > -1) {
  //   const liteReviewer = updatedReviewers[caapLiteIndex];
  //   if (liteReviewer.userData.subscriptionStatus === 'ACTIVE') {
  //     liteReviewer.userData.expirationDate = 'December 30, 2024';
  //   } else {
  //     // Remove CAAP Lite if not active
  //     updatedReviewers.splice(caapLiteIndex, 1);
  //   }
  // }

  // Update reviewers ref
  reviewers.value = updatedReviewers;
  isWaiting.value = false;
}

</script>